import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NavLogoComponent } from '../../atoms/nav-logo/nav-logo.component';
import { NavlinkComponent } from '../../atoms/navlink/navlink.component';
import { NavlinkToggleBtnComponent } from '../../atoms/navlink-toggle-btn/navlink-toggle-btn.component';
import { MatButtonModule } from '@angular/material/button';
import { FilledButtonComponent } from '../../atoms/filled-button/filled-button.component';
import { StrokedButtonComponent } from '../../atoms/stroked-button/stroked-button.component';
import { StoreImpersonationDropdownComponent } from '../store-impersonation-dropdown/store-impersonation-dropdown.component';
import { Store } from '../../../types';
import {
  GlobalDataService,
  SessionData,
} from '../../../services/global-data/global-data.service';
import { Router } from '@angular/router';
import { FilterDirectiveDirective } from '../../atoms/filter-directive/filter-directive.directive';
import { AsyncPipe } from '@angular/common';
import { BehaviorSubject, catchError, distinctUntilChanged, EMPTY, exhaustMap, fromEvent, merge, Subscription, switchMap, timer } from 'rxjs';
import { editorStoreIds, hasRole, hasStoreContext } from '../../../guards/auth/roles';
import { permissionRedirect, routePermission } from '../../../guards/administration/administration.guard';
import { AdministrationService } from '../../../services/administration/administration.service';
import { AdvisorSessionRecoveryService } from '../../../services/auth/advisor-session-recovery.service';
import { SamSessionService } from '../../../services/auth/sam-session.service';

export interface NavLinkProps {
  routerLinkName: string;
  linkText: string;
  guardFn: (sessionData: SessionData) => boolean;
}

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [
    RouterLink,
    NavLogoComponent,
    NavlinkComponent,
    NavlinkToggleBtnComponent,
    MatButtonModule,
    FilledButtonComponent,
    StrokedButtonComponent,
    StoreImpersonationDropdownComponent,
    FilterDirectiveDirective,
    AsyncPipe,
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.css',
})
export class MenuComponent implements OnInit, OnDestroy {
  hasRole = hasRole;
  hasStoreContext = hasStoreContext;

  sessionData$: BehaviorSubject<SessionData> =
    this.globalDataService.getSessionData();

  sessionDataSubscription?: Subscription;
  private permissionRefresh?: Subscription;
  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private globalDataService: GlobalDataService,
    private router: Router,
    private administrationService: AdministrationService,
    private advisorRecovery: AdvisorSessionRecoveryService,
    private samSession: SamSessionService
  ) {}

  showMenu: boolean = false;
  logoutError = '';

  isLoading: boolean = false;
  showDropDown: boolean = false;

  private readonly links: NavLinkProps[] = [
    { routerLinkName: 'overview', linkText: 'Overblik', guardFn: hasStoreContext },
    { routerLinkName: 'sendouts', linkText: 'Udsendelser', guardFn: session =>
      hasRole(session, 'SendoutManager') && !session.storeImpersonation },
    { routerLinkName: 'store-sendouts', linkText: 'Butikkens udsendelser', guardFn: hasStoreContext },
    { routerLinkName: 'select-store', linkText: 'Vælg butik', guardFn: session =>
      !session.storeImpersonation && (editorStoreIds(session).length > 1 ||
        (!hasStoreContext(session) && editorStoreIds(session).length > 0)) },
    { routerLinkName: 'templates', linkText: 'Skabeloner', guardFn: session => hasRole(session, 'TemplateManager') },
    { routerLinkName: 'manage-calendar', linkText: 'Kalender', guardFn: session => hasRole(session, 'SendoutManager') },
    { routerLinkName: 'manage-stores', linkText: 'Butikker', guardFn: session => hasRole(session, 'StoreManager') },
    { routerLinkName: 'manage-users', linkText: 'Brugere', guardFn: session => hasRole(session, 'UserManager') },
    { routerLinkName: 'lookup-member', linkText: 'Medlemsopslag', guardFn: session => hasRole(session, 'SendoutManager') },
  ];

  navLinksData: NavLinkProps[] = [];

  ngOnInit() {
    this.sessionDataSubscription = this.sessionData$.subscribe(session => {
      this.updateNavLinks(session);
      this.changeDetectorRef.markForCheck();
    });
    this.permissionRefresh = this.sessionData$.pipe(
      distinctUntilChanged((a, b) => a.userId === b.userId && a.sessionVersion === b.sessionVersion && a.initialized === b.initialized),
      switchMap(session => session.initialized && session.sessionVersion
        ? merge(timer(30_000, 30_000), fromEvent(window, 'focus')).pipe(
            exhaustMap(() => this.administrationService.getMyRoles().pipe(catchError(() => EMPTY)))
          )
        : EMPTY)
    ).subscribe(() => {
      const session = this.sessionData$.value;
      const route = this.router.routerState.snapshot.root.firstChild;
      const permission = route && routePermission(route);
      if (!session.initialized || permission !== 'no-access' || this.router.getCurrentNavigation()) return;
      const redirect = permissionRedirect(permission, session);
      if (redirect) void this.router.navigate(['/', redirect], { replaceUrl: true });
    });
  }

  ngOnDestroy(): void {
    this.sessionDataSubscription?.unsubscribe();
    this.permissionRefresh?.unsubscribe();
  }

  updateNavLinks(sessionData: SessionData) {
    this.navLinksData = this.links.filter((link) =>
      link.guardFn(sessionData)
    );
  }

  toggleShowDropDown() {
    this.showDropDown = !this.showDropDown;
  }

  onOutsideClick() {
    this.showDropDown = false;
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
  }

  disableMenu() {
    this.showMenu = false;
  }

  async logout() {
    this.logoutError = '';
    const original = this.globalDataService.getSessionData().value;
    const generation = this.globalDataService.getAuthenticationGeneration();
    const navigated = await this.router.navigate([''], { state: { samLogout: true } });
    if (!navigated) return;
    const current = this.globalDataService.getSessionData().value;
    if (!current.initialized || current.userId !== original.userId || current.userType !== original.userType ||
        current.sessionVersion !== original.sessionVersion || !this.globalDataService.isAuthenticationCurrent(generation)) return;
    try {
      const signedOut = await this.samSession.signOut();
      if (!signedOut) {
        this.logoutError = 'Sessionen blev ændret i en anden fane. Log ud igen, hvis du stadig vil afslutte denne session.';
        return;
      }
      sessionStorage.removeItem('msalHandleRedirect');
      this.advisorRecovery.clear();
    } catch {
      this.logoutError = 'Log ud kunne ikke gennemføres. Prøv igen.';
    }
  }

  storeClickForImpersonation(store: Store) {
    if (!hasRole(this.sessionData$.value, 'SendoutManager')) return;
    this.globalDataService.startStoreImpersonation(store);
    this.showDropDown = false;
    this.router.navigate(['overview']);
  }
}
